/**
 * Apply CDU list edits from a Request Detail edit session on Save Changes (#996 Phase 2).
 * Mirrors smallUpsEditSessionFlush. Note PUT (not PATCH) for unit updates, and the
 * reorder body key is `liquidCoolingCduIds`.
 */

import { api } from './api';
import {
  computeRepeatableOperations,
  type ComputedRepeatableOps,
} from './computeRepeatableOperations';
import { mirrorLiquidCoolingCduToOfflineRequestCache } from './liquidCoolingCduOfflineSync';
import { toCreateLiquidCoolingCduPayload } from './liquidCoolingCduPayload';
import { networkService } from './network';
import { offlineCache } from './offlineCache';
import { flushRepeatableInstanceAttachmentSectionRemaps } from './repeatableInstanceAttachmentRemapFlush';
import { deleteRepeatableUnitTolerating404 } from './repeatableUnitDelete';
import {
  LEGACY_LIQUID_COOLING_CDU_SYNTHETIC_ID,
  validateSessionLiquidCoolingCduNames,
} from '../components/liquidcoolingcdus/liquidCoolingCduListUtils';
import { LIQUID_COOLING_CDU_REPEATABLE_ATTACHMENT_BASE_SECTIONS } from '../utils/repeatableInstanceAttachmentSections';

import type { LiquidCoolingCduConfiguration } from '../types/liquidCoolingCdu';

function isPersistedLiquidCoolingCduId(id: number | undefined): id is number {
  return id != null && id > 0;
}

function isSessionOnlyLiquidCoolingCduId(id: number | undefined): boolean {
  return id == null || id <= 0 || id === LEGACY_LIQUID_COOLING_CDU_SYNTHETIC_ID;
}

/**
 * Canonicalize for content comparison: sort keys and drop undefined values so
 * two rows with identical CONTENT compare equal regardless of key insertion
 * order. Load-bearing since #997 Story 6.8 (mirror of `coolingSystemEditSessionFlush`):
 * the engine save bridge's baseline (raw API/mapper rows, identity keys first)
 * and the session rows (round-tripped through `descriptorInstanceToLiquidCoolingCdu`,
 * identity keys appended last) are different object lineages — a raw
 * `JSON.stringify` flagged every unit as changed, PUTting the whole list on save.
 */
function canonicalize(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(canonicalize);
  }
  if (value && typeof value === 'object') {
    const sorted: Record<string, unknown> = {};
    for (const key of Object.keys(value as Record<string, unknown>).sort()) {
      const entry = (value as Record<string, unknown>)[key];
      if (entry !== undefined) {
        sorted[key] = canonicalize(entry);
      }
    }
    return sorted;
  }
  return value;
}

function liquidCoolingCduContentKey(
  unit: LiquidCoolingCduConfiguration,
): string {
  // `requestId` excluded too (#997 Story 6.8, mirror cooling/large UPS/rack review):
  // the engine conversion stamps `parseInt('o-…') → 0` on offline-request session
  // rows, which would keep every untouched unit permanently "changed" against the
  // cached baseline.
  const {
    id: _id,
    requestId: _rid,
    createdAt: _ca,
    updatedAt: _ua,
    ...rest
  } = unit;
  return JSON.stringify(canonicalize(rest));
}

function liquidCoolingCduEqual(
  a: LiquidCoolingCduConfiguration[],
  b: LiquidCoolingCduConfiguration[],
): boolean {
  if (a.length !== b.length) {
    return false;
  }
  return a.every(
    (unit, index) =>
      liquidCoolingCduContentKey(unit) === liquidCoolingCduContentKey(b[index]),
  );
}

export function liquidCoolingCduEditSessionHasChanges(
  baseline: LiquidCoolingCduConfiguration[],
  session: LiquidCoolingCduConfiguration[],
): boolean {
  return !liquidCoolingCduEqual(baseline, session);
}

/**
 * Pure save-time diff for CDUs (#1501): consumed by this flush AND by the
 * aggregate save payload builder — one diff implementation, two consumers.
 */
export function computeLiquidCoolingCduOperations(
  baseline: LiquidCoolingCduConfiguration[],
  session: LiquidCoolingCduConfiguration[],
): ComputedRepeatableOps<LiquidCoolingCduConfiguration> {
  return computeRepeatableOperations(baseline, session, {
    isPersistedId: isPersistedLiquidCoolingCduId,
    isSessionOnlyId: isSessionOnlyLiquidCoolingCduId,
    contentKey: liquidCoolingCduContentKey,
    stripForUpdate: unit => {
      const {
        id: _id,
        createdAt: _ca,
        updatedAt: _ua,
        requestId: _rid,
        ...data
      } = unit;
      return data;
    },
  });
}

export async function flushLiquidCoolingCduEditSession(
  requestId: string,
  baseline: LiquidCoolingCduConfiguration[],
  session: LiquidCoolingCduConfiguration[],
  isOfflineRequest: boolean,
): Promise<LiquidCoolingCduConfiguration[]> {
  if (!liquidCoolingCduEditSessionHasChanges(baseline, session)) {
    return session;
  }

  const nameError = validateSessionLiquidCoolingCduNames(session);
  if (nameError) {
    throw new Error(nameError);
  }

  const idKey = String(requestId);
  const useOnlineApi = networkService.isConnected() && !isOfflineRequest;

  const ops = computeLiquidCoolingCduOperations(baseline, session);

  for (const deletedId of ops.deletes) {
    if (useOnlineApi) {
      await deleteRepeatableUnitTolerating404(
        `/requests/${idKey}/liquid-cooling-cdus/${deletedId}`,
      );
    } else {
      await offlineCache.addLiquidCoolingCduToOfflineQueue(
        idKey,
        'delete',
        undefined,
        deletedId,
      );
    }
  }

  const createdIdMap = new Map<number, number>();

  for (const { tempId, row } of ops.creates) {
    const payload = toCreateLiquidCoolingCduPayload({
      ...row,
      requestId: parseInt(idKey, 10) || row.requestId,
    });
    if (useOnlineApi) {
      const created = await api.post<LiquidCoolingCduConfiguration>(
        `/requests/${idKey}/liquid-cooling-cdus`,
        payload,
      );
      if (tempId != null && created.id) {
        createdIdMap.set(tempId, created.id);
      }
    } else {
      await offlineCache.addLiquidCoolingCduToOfflineQueue(
        idKey,
        'create',
        payload,
        tempId,
      );
    }
  }

  for (const { id, data } of ops.updates) {
    if (useOnlineApi) {
      await api.put<LiquidCoolingCduConfiguration>(
        `/requests/${idKey}/liquid-cooling-cdus/${id}`,
        data,
      );
    } else {
      await offlineCache.addLiquidCoolingCduToOfflineQueue(
        idKey,
        'update',
        data,
        id,
      );
    }
  }

  const tempIdByClientKey = new Map(
    ops.creates.map(c => [c.clientKey, c.tempId]),
  );
  const orderedPersistedIds = ops.orderRefs
    .map(ref => {
      if (typeof ref === 'number') {
        return ref;
      }
      const tempId = tempIdByClientKey.get(ref);
      return tempId != null && createdIdMap.has(tempId)
        ? createdIdMap.get(tempId)!
        : null;
    })
    .filter((id): id is number => id != null);

  if (
    orderedPersistedIds.length > 0 &&
    JSON.stringify(orderedPersistedIds) !==
      JSON.stringify(ops.baselineOrderInSession)
  ) {
    if (useOnlineApi) {
      await api.patch<LiquidCoolingCduConfiguration[]>(
        `/requests/${idKey}/liquid-cooling-cdus/reorder`,
        { liquidCoolingCduIds: orderedPersistedIds },
      );
    } else {
      await offlineCache.addLiquidCoolingCduToOfflineQueue(
        idKey,
        'reorder',
        undefined,
        undefined,
        orderedPersistedIds,
      );
    }
  }

  await flushRepeatableInstanceAttachmentSectionRemaps(
    idKey,
    createdIdMap,
    LIQUID_COOLING_CDU_REPEATABLE_ATTACHMENT_BASE_SECTIONS,
    useOnlineApi,
  );

  const normalizedSession = session.map(unit => {
    if (unit.id != null && createdIdMap.has(unit.id)) {
      return { ...unit, id: createdIdMap.get(unit.id)! };
    }
    return unit;
  });

  if (!useOnlineApi) {
    await offlineCache.updateCachedRequestLiquidCoolingCdus(
      idKey,
      normalizedSession,
    );
  } else {
    await mirrorLiquidCoolingCduToOfflineRequestCache(idKey, normalizedSession);
  }

  return normalizedSession;
}
